import React, { useState } from 'react'
import { Message, MessageType, ConversationType } from '../../types/messaging'
import { messagingService } from '../../services/messagingService'
import { cn } from '../../lib/utils'
import { 
  MoreVertical, 
  Edit, 
  Trash2, 
  Reply, 
  Smile,
  Check,
  CheckCheck
} from 'lucide-react'

interface MessageBubbleProps {
  message: Message
  currentUserId: number
  isConsecutive?: boolean
  conversationType?: ConversationType
  onEdit?: (message: Message) => void
  onDelete?: (message: Message) => void
  onReply?: (message: Message) => void
  onReact?: (message: Message, reactionType: 'like' | 'love' | 'wow' | 'sad' | 'angry' | 'haha') => void
  reactionCounts?: {
    like: number
    love: number
    wow: number
    sad: number
    angry: number
    haha: number
  }
  currentUserReaction?: string | null
  isReacting?: boolean
  className?: string
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  currentUserId,
  isConsecutive = false,
  conversationType,
  onEdit,
  onDelete,
  onReply,
  onReact,
  reactionCounts: propReactionCounts,
  currentUserReaction: propCurrentUserReaction,
  isReacting: propIsReacting,
  className
}) => {
  const [showActions, setShowActions] = useState(false)
  const [showReactions, setShowReactions] = useState(false)
  const isOwnMessage = message.senderId === currentUserId
  const isDeleted = message.deletedAt !== null
  const isEdited = message.isEdited
  const isGroupChat = conversationType === ConversationType.GROUP
  
  const getSenderName = () => {
    if (message.senderDisplayName) {
      return message.senderDisplayName
    }
    if (message.sender?.firstName || message.sender?.lastName) {
      return `${message.sender.firstName || ''} ${message.sender.lastName || ''}`.trim()
    }
    return message.senderUsername || 'Người dùng'
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  const isImageUrl = (url: string): boolean => {
    if (!url) return false
    const lowerUrl = url.toLowerCase()
    return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(lowerUrl)
  }

  const isVideoUrl = (url: string): boolean => {
    if (!url) return false
    const lowerUrl = url.toLowerCase()
    return /\.(mp4|webm|mov|m4v|avi|mkv)(\?|$)/i.test(lowerUrl)
  }

  const getReadStatusIcon = () => {
    if (!isOwnMessage) return null
    
    const readByOthers = message.readBy?.filter(read => read.userId !== currentUserId) || []
    
    if (readByOthers.length === 0) {
      return <Check className="w-3 h-3 text-gray-400" aria-label="Đã gửi" />
    } else {
      return <CheckCheck className="w-3 h-3 text-blue-500" aria-label="Đã đọc" />
    }
  }

  const handleActionClick = (action: string) => {
    setShowActions(false)
    
    switch (action) {
      case 'edit':
        onEdit?.(message)
        break
      case 'delete':
        onDelete?.(message)
        break
      case 'reply':
        onReply?.(message)
        break
      case 'react':
        setShowReactions(true)
        break
    }
  }

  const handleReactionClick = (emoji: string) => {
    const map: Record<string, 'like' | 'love' | 'wow' | 'sad' | 'angry' | 'haha'> = {
      '👍': 'like',
      '❤️': 'love',
      '😂': 'haha',
      '😮': 'wow',
      '😢': 'sad',
      '😡': 'angry',
      '😀': 'like'
    }
    const type = map[emoji]
    if (type && onReact) {
      onReact(message, type)
    }
    setShowReactions(false)
  }

  const reactions = ['😀', '❤️', '👍', '👎', '😂', '😮', '😢', '😡']

  return (
    <div className={cn("group relative", className)}>
      <div
        className={cn(
          "flex items-end space-x-2 max-w-xs lg:max-w-md",
          isOwnMessage ? "ml-auto flex-row-reverse space-x-reverse" : "mr-auto"
        )}
      >
        {/* Avatar */}
        {(!isConsecutive || isGroupChat) && (
          <div className="flex-shrink-0">
            {message.senderAvatarUrl ? (
              <img
                src={message.senderAvatarUrl}
                alt={getSenderName()}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                <span className="text-white font-semibold text-xs">
                  {getSenderName().charAt(0).toUpperCase() || '?'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Message Content */}
        <div className="flex flex-col">
          {/* Sender name - always show for group chats, show for non-consecutive messages in direct chats */}
          {(isGroupChat || !isConsecutive) && (
            <span className={cn(
              "text-xs font-semibold mb-1 px-1",
              isOwnMessage 
                ? "text-primary-600 dark:text-primary-400" 
                : "text-gray-700 dark:text-gray-300"
            )}>
              {isOwnMessage ? 'Bạn' : getSenderName()}
            </span>
          )}

          {/* Message bubble */}
          <div
            className={cn(
              "relative px-4 py-2 rounded-2xl shadow-sm",
              isOwnMessage
                ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white"
                : "bg-white border border-gray-200 text-gray-900",
              isConsecutive && !isOwnMessage && "ml-10",
              !isConsecutive && (isOwnMessage ? 'rounded-tr-sm' : 'rounded-tl-sm')
            )}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
          >
            {isDeleted ? (
              <div className="italic text-gray-500">
                Tin nhắn đã bị xóa
              </div>
            ) : (
              <>
                {/* Reply To Section */}
                {message.replyTo && (
                  <div className={cn(
                    "mb-2 pb-2 border-l-2 pl-3 rounded",
                    isOwnMessage 
                      ? "border-white/30 bg-white/10" 
                      : "border-gray-300 bg-gray-50"
                  )}>
                    <div className={cn(
                      "text-xs font-semibold mb-1",
                      isOwnMessage ? "text-white/80" : "text-gray-600"
                    )}>
                      {message.replyTo.senderUsername}
                    </div>
                    <div className={cn(
                      "text-xs line-clamp-2",
                      isOwnMessage ? "text-white/70" : "text-gray-500"
                    )}>
                      {message.replyTo.messageType === MessageType.IMAGE && '🖼️ '}
                      {message.replyTo.messageType === MessageType.VIDEO && '🎥 '}
                      {message.replyTo.messageType === MessageType.FILE && '📎 '}
                      {message.replyTo.contentPreview}
                    </div>
                  </div>
                )}

                {(() => {
                  const urls: string[] = (message.attachmentUrls && message.attachmentUrls.length > 0)
                    ? message.attachmentUrls
                    : (message.attachments?.map(a => a.fileUrl).filter(Boolean) as string[] | undefined) || []

                  const typeStr = String(message.messageType)
                  
                  const attachments = message.attachments || []
                  const hasImageAttachments = attachments.some(a => 
                    a.fileType?.startsWith('image/') || (a.fileUrl && isImageUrl(a.fileUrl))
                  )
                  const hasVideoAttachments = attachments.some(a => 
                    a.fileType?.startsWith('video/') || (a.fileUrl && isVideoUrl(a.fileUrl))
                  )
                  
                  const hasMediaUrls = urls.some(url => isImageUrl(url) || isVideoUrl(url))
                  const isMedia = typeStr === 'image' || typeStr === 'video' || hasImageAttachments || hasVideoAttachments || hasMediaUrls

                  if (isMedia && urls.length > 0) {
                    return (
                      <div className="space-y-2">
                        {urls.map((url, index) => {
                          const attachment = attachments[index]
                          const attachmentFileType = attachment?.fileType
                          const urlIsVideo = isVideoUrl(url)
                          const urlIsImage = isImageUrl(url)
                          const typeIsVideo = typeStr === 'video' || attachmentFileType?.startsWith('video/')
                          const typeIsImage = typeStr === 'image' || attachmentFileType?.startsWith('image/')
                          
                          const shouldShowVideo = typeIsVideo || urlIsVideo
                          const shouldShowImage = !shouldShowVideo && (typeIsImage || urlIsImage)
                          
                          return (
                            <div key={index} className="rounded-lg overflow-hidden">
                              {shouldShowVideo ? (
                                <video
                                  src={url}
                                  className="max-w-full h-auto rounded-lg"
                                  controls
                                />
                              ) : shouldShowImage ? (
                                <img
                                  src={url}
                                  alt={`Attachment ${index + 1}`}
                                  className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(url, '_blank')}
                                />
                              ) : (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  <span className="text-sm">📎</span>
                                  <span className="text-sm truncate">
                                    {attachment?.fileName || url.split('/').pop()}
                                  </span>
                                </a>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  }

                  // Otherwise, show message content (text) and any non-media attachments as links
                  return (
                    <>
                      {message.content && (
                        <div className="whitespace-pre-wrap break-words leading-relaxed">
                          {message.content}
                        </div>
                      )}
                      {urls.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {urls.map((url, index) => (
                            <div key={index} className="rounded-lg overflow-hidden">
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                <span className="text-sm">📎</span>
                                <span className="text-sm truncate">
                                  {url.split('/').pop()}
                                </span>
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )
                })()}

                {/* Reactions */}
                {(() => {
                  const reactions = propReactionCounts || message.reactionCounts
                  const hasReactions = reactions && Object.values(reactions).some(c => c > 0)
                  return hasReactions && (
                    <div className={cn(
                      "absolute -bottom-3",
                      isOwnMessage ? "right-2" : "left-2"
                    )}>
                      <div className={cn(
                        "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs shadow bg-white text-gray-700"
                      )}>
                        {reactions.like > 0 && <span>👍 {reactions.like}</span>}
                        {reactions.love > 0 && <span>❤️ {reactions.love}</span>}
                        {reactions.haha > 0 && <span>😂 {reactions.haha}</span>}
                        {reactions.wow > 0 && <span>😮 {reactions.wow}</span>}
                        {reactions.sad > 0 && <span>😢 {reactions.sad}</span>}
                        {reactions.angry > 0 && <span>😡 {reactions.angry}</span>}
                      </div>
                    </div>
                  )
                })()}
              </>
            )}

            {/* Message actions */}
            {showActions && !isDeleted && (
              <div className={cn(
                "absolute",
                isOwnMessage ? "-left-2 top-1/2 -translate-y-1/2" : "-right-2 top-1/2 -translate-y-1/2"
              )}>
                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full shadow-lg p-1 backdrop-blur-sm">
                  <button
                    onClick={() => handleActionClick('react')}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                    title="Cảm xúc"
                  >
                    <Smile className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleActionClick('reply')}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                    title="Trả lời"
                  >
                    <Reply className="w-4 h-4 text-gray-600" />
                  </button>
                  {isOwnMessage && (
                    <>
                      <button
                        onClick={() => handleActionClick('edit')}
                        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        title="Chỉnh sửa"
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleActionClick('delete')}
                        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        title="Xóa"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Reaction picker */}
            {showReactions && (
              <div
                className={cn(
                  "absolute z-20 bg-white border border-gray-200 rounded-2xl shadow-xl p-1 transition-opacity duration-200 ease-in-out",
                  isOwnMessage ? "right-0 bottom-[-44px]" : "left-0 bottom-[-44px]",
                  showReactions ? "opacity-100" : "opacity-0"
                )}
                onMouseEnter={() => setShowReactions(true)}
                onMouseLeave={() => setShowReactions(false)}
              >
                <div className="flex items-center gap-1">
                  {reactions.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleReactionClick(emoji)}
                      className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-lg transition-transform transform hover:scale-110"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Message metadata */}
          <div className={cn(
            "flex items-center space-x-1 mt-1 px-1",
            isOwnMessage ? "justify-end" : "justify-start"
          )}>
            <span className="text-xs text-gray-500">
              {formatTime(message.createdAt)}
            </span>
            {isEdited && (
              <span className="text-xs text-gray-500">(đã sửa)</span>
            )}
            {getReadStatusIcon()}
          </div>
        </div>
      </div>
    </div>
  )
}
